/**
 * Parser multi-feature para CARGA MASIVA de predios (KML / KMZ / GeoJSON).
 *
 * A diferencia de parseUploadedGeoJSON (single-feature), aquí cada POLÍGONO
 * del archivo es un predio. Líneas y puntos NO son predios → se descartan con
 * warning. Valida CRS (rechaza no-WGS84 / coords proyectadas).
 *
 * Diseño: `extractPolygonItems` es PURO (opera sobre GeoJSON ya parseado) y se
 * testea sin DOM ni red. `parseLayerFile` es la capa browser (detecta formato,
 * descomprime KMZ con jszip, convierte KML con @tmcw/togeojson vía DOMParser).
 */
import JSZip from 'jszip';
import { kml as kmlToGeoJSON } from '@tmcw/togeojson';
import { isWithinMexicoBounds, type SimpleGeometry } from './parseUploadedGeoJSON';

export interface LayerFeatureItem {
  /** Geometría del predio (Polygon | MultiPolygon) WGS84. */
  geojson_geom: SimpleGeometry;
  /** Feature original (para auditoría / re-derivar). */
  geojson_raw: unknown;
  /** Nombre/etiqueta best-effort desde las props del KML/GeoJSON (display + default num_parcela). */
  nombre: string | null;
}

export interface LayerParseResult {
  ok: true;
  items: LayerFeatureItem[];
  /** Avisos: features no-polígono descartados, CRS, etc. */
  warnings: string[];
  /** Cuántos features se descartaron por no ser polígonos. */
  skipped: number;
}

export interface LayerParseError {
  ok: false;
  error: string;
}

/** CRS aceptados (RFC7946 = WGS84). Los demás se rechazan. */
const CRS84_NAMES = new Set([
  'urn:ogc:def:crs:ogc:1.3:crs84',
  'urn:ogc:def:crs:epsg::4326',
  'epsg:4326',
  'crs84',
  'wgs84',
]);

function readNombre(props: unknown): string | null {
  if (typeof props !== 'object' || props === null) return null;
  const p = props as Record<string, unknown>;
  for (const k of ['nombre', 'name', 'Name', 'NAME', 'num_parcela', 'numParcela', 'title']) {
    const v = p[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Recolecta las geometrías Polygon/MultiPolygon de una geometría, desempaquetando
 * GeometryCollection de forma recursiva. Cubre el caso KML <MultiGeometry> con
 * varios polígonos, que @tmcw/togeojson emite como un Feature cuya geometry es
 * GeometryCollection — antes se descartaba en silencio. Líneas/puntos dentro del
 * collection se ignoran (no son predios).
 */
/**
 * Detecta coordenadas inválidas/no numéricas (puntos con <2 componentes, NaN,
 * Infinity, o no-número). Sirve para distinguir un archivo CORRUPTO de uno
 * PROYECTADO (fuera de bounds pero numérico) y dar un mensaje preciso.
 */
function hasInvalidCoords(geom: SimpleGeometry): boolean {
  const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;
  for (const poly of polys) {
    for (const ring of poly) {
      for (const pt of ring) {
        if (
          !Array.isArray(pt) || pt.length < 2 ||
          typeof pt[0] !== 'number' || typeof pt[1] !== 'number' ||
          !Number.isFinite(pt[0]) || !Number.isFinite(pt[1])
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function collectPolygons(geom: unknown): SimpleGeometry[] {
  if (typeof geom !== 'object' || geom === null) return [];
  const g = geom as { type?: unknown; coordinates?: unknown; geometries?: unknown };
  if (
    (g.type === 'Polygon' || g.type === 'MultiPolygon') &&
    Array.isArray(g.coordinates) &&
    g.coordinates.length > 0
  ) {
    return [g as SimpleGeometry];
  }
  if (g.type === 'GeometryCollection' && Array.isArray(g.geometries)) {
    return (g.geometries as unknown[]).flatMap(collectPolygons);
  }
  return [];
}

/**
 * Núcleo PURO. Toma un objeto GeoJSON (FeatureCollection | Feature | Geometry
 * | GeometryCollection) y extrae los polígonos como items de predio.
 * Valida CRS (miembro `crs` + bounds de México). Rechaza el archivo completo
 * si detecta CRS proyectado (un polígono fuera del bbox de México).
 */
export function extractPolygonItems(raw: unknown): LayerParseResult | LayerParseError {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: 'No es GeoJSON válido.' };
  }
  const obj = raw as { type?: unknown; crs?: unknown; features?: unknown; geometries?: unknown; geometry?: unknown };

  // CRS explícito no-WGS84 → rechazo (RFC7946 es WGS84; algunos exports traen crs).
  const crsName = (() => {
    const crs = obj.crs as { properties?: { name?: unknown } } | undefined;
    const n = crs?.properties?.name;
    return typeof n === 'string' ? n.toLowerCase().trim() : null;
  })();
  if (crsName && !CRS84_NAMES.has(crsName)) {
    return { ok: false, error: `CRS no soportado: "${crsName}". Reproyecta a WGS84 (EPSG:4326) antes de subir.` };
  }

  // Reunir la lista de "features" a inspeccionar (cada uno con geometry + props).
  type RawFeature = { geometry?: unknown; properties?: unknown; type?: unknown; coordinates?: unknown };
  let rawFeatures: RawFeature[];
  switch (obj.type) {
    case 'FeatureCollection': {
      if (!Array.isArray(obj.features) || obj.features.length === 0) {
        return { ok: false, error: 'FeatureCollection vacía.' };
      }
      rawFeatures = obj.features as RawFeature[];
      break;
    }
    case 'Feature':
      rawFeatures = [obj as RawFeature];
      break;
    case 'GeometryCollection': {
      const geoms = Array.isArray(obj.geometries) ? obj.geometries : [];
      rawFeatures = (geoms as unknown[]).map((g) => ({ geometry: g, properties: null }));
      break;
    }
    case 'Polygon':
    case 'MultiPolygon':
    case 'Point':
    case 'LineString':
    case 'MultiPoint':
    case 'MultiLineString':
      rawFeatures = [{ geometry: obj, properties: null }];
      break;
    default:
      return { ok: false, error: `Tipo GeoJSON no soportado: ${String(obj.type)}.` };
  }

  const items: LayerFeatureItem[] = [];
  const warnings: string[] = [];
  let skipped = 0;

  for (const feat of rawFeatures) {
    // Un Feature tiene .geometry; una Geometry directa ES la geometría.
    // collectPolygons desempaqueta GeometryCollection (KML <MultiGeometry>).
    const geom = feat.geometry ?? feat;
    const polys = collectPolygons(geom);

    if (polys.length === 0) {
      skipped += 1; // líneas/puntos/null/geom vacía no son predios
      continue;
    }

    for (const simple of polys) {
      // Distinguir CORRUPTO (coords no numéricas) de PROYECTADO (numérico fuera
      // de bounds) para no dar un mensaje de "no es WGS84" cuando el archivo
      // está mal formado.
      if (hasInvalidCoords(simple)) {
        return {
          ok: false,
          error: 'Geometría con coordenadas inválidas o no numéricas — el archivo está corrupto o mal formado.',
        };
      }
      if (!isWithinMexicoBounds(simple)) {
        // Numérico pero fuera del bbox de México = casi seguro CRS proyectado.
        return {
          ok: false,
          error: 'Coordenadas fuera del rango de México (lat 14-33, lng -118 a -86). ' +
                 'El archivo probablemente NO está en WGS84/EPSG:4326 — reproyéctalo antes de subir.',
        };
      }
      items.push({
        geojson_geom: simple,
        geojson_raw: feat.geometry ? feat : { type: 'Feature', geometry: simple, properties: feat.properties ?? null },
        nombre: readNombre(feat.properties),
      });
    }
  }

  if (items.length === 0) {
    return { ok: false, error: 'El archivo no contiene polígonos (los predios deben ser Polygon o MultiPolygon).' };
  }
  if (skipped > 0) {
    warnings.push(`Se ignoraron ${skipped} elemento(s) que no son polígonos (líneas/puntos).`);
  }

  return { ok: true, items, warnings, skipped };
}

/* ==========================================================================
 * Variante para CAPAS INFORMATIVAS (líneas/puntos/polígonos de referencia).
 *
 * A diferencia de extractPolygonItems, aquí conservamos CUALQUIER geometría
 * (Point/LineString/Polygon/Multi*) y NO fallamos por ausencia de polígonos.
 * Cada geometría se vuelve un capa_feature. Núcleo puro testeable.
 * ========================================================================== */

/** Bounds aprox de México (espejo de parseUploadedGeoJSON; aquí para geometrías genéricas). */
const MX_LAT_MIN = 14;
const MX_LAT_MAX = 33;
const MX_LNG_MIN = -118;
const MX_LNG_MAX = -86;

/** Geometría GeoJSON de cualquier tipo simple (sin GeometryCollection: se desempaqueta). */
export type AnyGeometry =
  | { type: 'Point'; coordinates: number[] }
  | { type: 'MultiPoint'; coordinates: number[][] }
  | { type: 'LineString'; coordinates: number[][] }
  | { type: 'MultiLineString'; coordinates: number[][][] }
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export interface CapaFeatureItem {
  /** Geometría de la feature (cualquier tipo) WGS84. */
  geojson_geom: AnyGeometry;
  /** Tipo de geometría (para agrupar render fill/line/circle). */
  geometry_type: AnyGeometry['type'];
  /** Feature original (auditoría). */
  geojson_raw: unknown;
  /** Nombre/etiqueta best-effort desde las props. */
  nombre: string | null;
  /** Properties del feature original (se persisten en capa_features.properties). */
  properties: Record<string, unknown> | null;
}

export interface CapaFeatureParseResult {
  ok: true;
  items: CapaFeatureItem[];
  warnings: string[];
  /** Cuántos elementos se descartaron por geometría vacía/no soportada. */
  skipped: number;
}

const SUPPORTED_GEOM_TYPES = new Set([
  'Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon',
]);

/**
 * Profundidad de anidación de `coordinates` por tipo: Point = una posición
 * directa (0), Line/MultiPoint = posición[] (1), Polygon/MultiLineString =
 * posición[][] (2), MultiPolygon = posición[][][] (3). Caminar por profundidad
 * conocida evita confundir "posición con primer elemento no numérico" (Point
 * corrupto ['x','y']) con un nivel de anidación.
 */
const GEOM_DEPTH: Record<AnyGeometry['type'], number> = {
  Point: 0, MultiPoint: 1, LineString: 1, MultiLineString: 2, Polygon: 2, MultiPolygon: 3,
};

function forEachPositionAtDepth(coords: unknown, depth: number, cb: (pos: unknown) => void): void {
  if (depth === 0) { cb(coords); return; }
  if (!Array.isArray(coords)) return;
  for (const c of coords) forEachPositionAtDepth(c, depth - 1, cb);
}

function forEachPosition(geom: AnyGeometry, cb: (pos: unknown) => void): void {
  forEachPositionAtDepth(geom.coordinates, GEOM_DEPTH[geom.type], cb);
}

function geomHasInvalidPosition(geom: AnyGeometry): boolean {
  let invalid = false;
  forEachPosition(geom, (pos) => {
    if (
      !Array.isArray(pos) || pos.length < 2 ||
      typeof pos[0] !== 'number' || typeof pos[1] !== 'number' ||
      !Number.isFinite(pos[0]) || !Number.isFinite(pos[1])
    ) {
      invalid = true;
    }
  });
  return invalid;
}

function geomWithinMexico(geom: AnyGeometry): boolean {
  let within = true;
  forEachPosition(geom, (pos) => {
    if (!Array.isArray(pos) || pos.length < 2) { within = false; return; }
    const lng = pos[0] as number;
    const lat = pos[1] as number;
    if (lng < MX_LNG_MIN || lng > MX_LNG_MAX || lat < MX_LAT_MIN || lat > MX_LAT_MAX) within = false;
  });
  return within;
}

/** Recolecta geometrías simples soportadas, desempaquetando GeometryCollection recursivamente. */
function collectGeoms(geom: unknown): AnyGeometry[] {
  if (typeof geom !== 'object' || geom === null) return [];
  const g = geom as { type?: unknown; coordinates?: unknown; geometries?: unknown };
  if (
    typeof g.type === 'string' && SUPPORTED_GEOM_TYPES.has(g.type) &&
    Array.isArray(g.coordinates) && g.coordinates.length > 0
  ) {
    return [g as AnyGeometry];
  }
  if (g.type === 'GeometryCollection' && Array.isArray(g.geometries)) {
    return (g.geometries as unknown[]).flatMap(collectGeoms);
  }
  return [];
}

/** Normaliza un objeto GeoJSON a la lista de features crudos a inspeccionar. */
type RawFeatureLike = { geometry?: unknown; properties?: unknown; type?: unknown; coordinates?: unknown };
function toRawFeatures(
  obj: { type?: unknown; features?: unknown; geometries?: unknown },
): RawFeatureLike[] | LayerParseError {
  switch (obj.type) {
    case 'FeatureCollection': {
      if (!Array.isArray(obj.features) || obj.features.length === 0) {
        return { ok: false, error: 'FeatureCollection vacía.' };
      }
      return obj.features as RawFeatureLike[];
    }
    case 'Feature':
      return [obj as RawFeatureLike];
    case 'GeometryCollection': {
      const geoms = Array.isArray(obj.geometries) ? obj.geometries : [];
      return (geoms as unknown[]).map((g) => ({ geometry: g, properties: null }));
    }
    case 'Point':
    case 'MultiPoint':
    case 'LineString':
    case 'MultiLineString':
    case 'Polygon':
    case 'MultiPolygon':
      return [{ geometry: obj as unknown, properties: null }];
    default:
      return { ok: false, error: `Tipo GeoJSON no soportado: ${String(obj.type)}.` };
  }
}

/**
 * Núcleo PURO para capas informativas. Conserva Point/Line/Polygon/Multi*,
 * desempaqueta GeometryCollection, valida CRS + bounds, y NO falla por ausencia
 * de polígonos (solo falla si no hay NINGUNA geometría válida).
 */
export function extractFeatureItems(raw: unknown): CapaFeatureParseResult | LayerParseError {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: 'No es GeoJSON válido.' };
  }
  const obj = raw as { type?: unknown; crs?: unknown; features?: unknown; geometries?: unknown };

  // CRS explícito no-WGS84 → rechazo (mismo criterio que extractPolygonItems).
  const crsName = (() => {
    const crs = obj.crs as { properties?: { name?: unknown } } | undefined;
    const n = crs?.properties?.name;
    return typeof n === 'string' ? n.toLowerCase().trim() : null;
  })();
  if (crsName && !CRS84_NAMES.has(crsName)) {
    return { ok: false, error: `CRS no soportado: "${crsName}". Reproyecta a WGS84 (EPSG:4326) antes de subir.` };
  }

  const rawFeatures = toRawFeatures(obj);
  if (!Array.isArray(rawFeatures)) return rawFeatures; // LayerParseError

  const items: CapaFeatureItem[] = [];
  const warnings: string[] = [];
  let skipped = 0;

  for (const feat of rawFeatures) {
    const geom = feat.geometry ?? feat;
    const geoms = collectGeoms(geom);
    if (geoms.length === 0) {
      skipped += 1; // geometría vacía / no soportada
      continue;
    }
    for (const simple of geoms) {
      if (geomHasInvalidPosition(simple)) {
        return {
          ok: false,
          error: 'Geometría con coordenadas inválidas o no numéricas — el archivo está corrupto o mal formado.',
        };
      }
      if (!geomWithinMexico(simple)) {
        return {
          ok: false,
          error: 'Coordenadas fuera del rango de México (lat 14-33, lng -118 a -86). ' +
                 'El archivo probablemente NO está en WGS84/EPSG:4326 — reproyéctalo antes de subir.',
        };
      }
      const props = (typeof feat.properties === 'object' && feat.properties !== null)
        ? (feat.properties as Record<string, unknown>)
        : null;
      items.push({
        geojson_geom: simple,
        geometry_type: simple.type,
        geojson_raw: feat.geometry ? feat : { type: 'Feature', geometry: simple, properties: feat.properties ?? null },
        nombre: readNombre(feat.properties),
        properties: props,
      });
    }
  }

  if (items.length === 0) {
    return { ok: false, error: 'El archivo no contiene geometrías válidas (puntos, líneas o polígonos).' };
  }
  if (skipped > 0) {
    warnings.push(`Se ignoraron ${skipped} elemento(s) sin geometría válida.`);
  }

  return { ok: true, items, warnings, skipped };
}

/** Detecta si un ArrayBuffer empieza con la firma ZIP ('PK\x03\x04'). */
function looksLikeZip(buf: ArrayBuffer): boolean {
  const b = new Uint8Array(buf.slice(0, 4));
  return b[0] === 0x50 && b[1] === 0x4b && (b[2] === 0x03 || b[2] === 0x05 || b[2] === 0x07);
}

function kmlDocToFeatureCollection(kmlText: string): unknown {
  // DOMParser existe en el browser. (El núcleo testeable no lo usa.)
  const doc = new DOMParser().parseFromString(kmlText, 'text/xml');
  return kmlToGeoJSON(doc);
}

/**
 * Capa browser compartida: recibe el File del dropzone, detecta formato
 * (.geojson/.json, .kml, .kmz) y devuelve el objeto GeoJSON parseado.
 */
async function readFileToGeoJSON(file: File): Promise<{ ok: true; json: unknown } | LayerParseError> {
  const name = file.name.toLowerCase();
  try {
    if (name.endsWith('.kmz') || (await isZipFile(file))) {
      const zip = await JSZip.loadAsync(await file.arrayBuffer());
      const kmlEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith('.kml'));
      if (!kmlEntry) return { ok: false, error: 'El KMZ no contiene un archivo .kml.' };
      return { ok: true, json: kmlDocToFeatureCollection(await kmlEntry.async('text')) };
    }
    if (name.endsWith('.kml')) {
      return { ok: true, json: kmlDocToFeatureCollection(await file.text()) };
    }
    // GeoJSON / JSON
    const text = await file.text();
    try {
      return { ok: true, json: JSON.parse(text) };
    } catch {
      return { ok: false, error: 'El archivo no es JSON válido ni KML/KMZ.' };
    }
  } catch (e) {
    return { ok: false, error: `No se pudo leer el archivo: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/**
 * Capa browser (PREDIOS operativos): cada polígono = un predio. Delega a
 * extractPolygonItems. Soporta .geojson/.json, .kml y .kmz.
 */
export async function parseLayerFile(file: File): Promise<LayerParseResult | LayerParseError> {
  const r = await readFileToGeoJSON(file);
  if (!r.ok) return r;
  return extractPolygonItems(r.json);
}

/**
 * Capa browser (CAPAS INFORMATIVAS): conserva cualquier geometría. Delega a
 * extractFeatureItems. Mismo soporte de formatos.
 */
export async function parseLayerFileFeatures(file: File): Promise<CapaFeatureParseResult | LayerParseError> {
  const r = await readFileToGeoJSON(file);
  if (!r.ok) return r;
  return extractFeatureItems(r.json);
}

async function isZipFile(file: File): Promise<boolean> {
  try {
    const head = await file.slice(0, 4).arrayBuffer();
    return looksLikeZip(head);
  } catch {
    return false;
  }
}
